import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'store/store';
import { getFeatureFlagByNameSelector } from 'store/selectors';
import { KEY_CODES } from 'utility/constants';
import { checkStores, setDataEmTag } from 'utility/utility';
import ErrorBoundaryWithLogger from 'common/ErrorBoundary/ErrorBoundary';
import useLogger from 'hooks/useLogger';
import MenuContainer from './MenuContainer';
import './DesktopNav.scss';

export default function DesktopNav({ desktopNavData, viewTopNavAnalyticsEvent }: any) {
  const { error } = useLogger();
  const [showSubMenu, setShowSubMenu] = useState(false);
  const firstSubLinkRef = useRef<HTMLAnchorElement | null>(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState<number | null>(0);
  const [keyBoardNavigation, setkeyBoardNavigation] = useState<boolean>(false);
  const navigationOnHoverEnabled = useAppSelector(
    getFeatureFlagByNameSelector('navigationOnHoverEnabled'),
  );

  useEffect(() => {
    if (document && document.body.classList.contains('navigation-open')) {
      document.body.classList.remove('navigation-open');
    }
  }, []);

  useEffect(() => {
    if (keyBoardNavigation === true) {
      // focus the first link in the menu if keyboard navigation was used
      firstSubLinkRef.current?.focus();
    }
  });

  const closeMenu = (): void => {
    setShowSubMenu(false);
  };

  const handleMouseLeaveNavigation = () => {
    if (showSubMenu && navigationOnHoverEnabled) {
      closeMenu();
    }
  };

  const openMenu = (index: number): void => {
    viewTopNavAnalyticsEvent();
    if (!showSubMenu) {
      setShowSubMenu(true);
    }
    setCurrentCategoryIndex(index);
  };

  const changeFocus = (offset = 0): void => {
    if (currentCategoryIndex !== null) {
      document.getElementById(`desktop-nav-l0-${offset}`)?.focus();
    }
  };

  const handleMouseEnter = (index: number): void => {
    if (navigationOnHoverEnabled) {
      setkeyBoardNavigation(false);
      openMenu(index);
    }
  };

  const handleMouseClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    index: number,
  ): void => {
    if (currentCategoryIndex === index && showSubMenu) {
      return;
    }
    event.preventDefault();
    setkeyBoardNavigation(false);
    if (desktopNavData[index].csvData && desktopNavData[index].csvData.length > 0) {
      openMenu(index);
    } else {
      window.location.href = desktopNavData[index].link;
    }
  };

  const handleKeyDownPrimaryCategory = (event: React.KeyboardEvent, index: number) => {
    if (event.keyCode === KEY_CODES.SPACE) {
      // prevents the spacebar from scrolling
      event.preventDefault();
      setkeyBoardNavigation(true);
      openMenu(index);
    }
    if (event.keyCode === KEY_CODES.ESCAPE) {
      closeMenu();
    }
  };

  const primaryCategories = () => {
    return desktopNavData?.map((data: any, index: number) => {
      // In order to be able to catch the error with ErrorBoundaryWithLogger and to avoid the component from breaking
      // We need to catch the error in try-catch and still wrap with ErrorBoundaryWithLogger
      try {
        return (
          <a
            key={data.title}
            className={`desktop-nav-container__nav--list-item hmf-p-xxs ${
              index === currentCategoryIndex && showSubMenu ? 'active' : ''
            } ${data.title.toLowerCase() === 'clearance' || data?.highlight ? 'clearance' : ''} `}
            href={checkStores(data.link)}
            id={`desktop-nav-l0-${index}`}
            onClick={(event) => handleMouseClick(event, index)}
            onMouseEnter={() => handleMouseEnter(index)}
            onKeyDown={(event) => handleKeyDownPrimaryCategory(event, index)}
            aria-current={index === currentCategoryIndex}
            aria-expanded={index === currentCategoryIndex}
            data-em={setDataEmTag('TopNav', data.title)}>
            <div className="desktop-nav-container__nav--list-item hidden-item hmf-subheader-bold-m">
              {data.title}
            </div>
            {data.title}
          </a>
        );
      } catch (e) {
        error('DesktopNav primaryCategory', e);
        return null;
      }
    });
  };

  return (
    <div className="desktop-nav-container" onMouseLeave={handleMouseLeaveNavigation}>
      <div className="desktop-nav-border-container">
        <nav
          className={`desktop-nav-container__nav hmf-subheader-m hmf-py-xxs ${
            currentCategoryIndex === null ? '' : 'active'
          }`}>
          {primaryCategories()}
        </nav>
      </div>
      <ErrorBoundaryWithLogger componentId="DesktopNav-MenuContainer">
        <MenuContainer
          desktopNavData={desktopNavData}
          index={currentCategoryIndex as number}
          showSubMenu={showSubMenu}
          closeMenu={closeMenu}
          changeFocus={changeFocus}
          handleMouseLeaveNavigation={handleMouseLeaveNavigation}
          firstSubLinkRef={firstSubLinkRef}
        />
      </ErrorBoundaryWithLogger>
    </div>
  );
}
